import axios from 'axios';
import ConstructorIOClient from '@constructor-io/constructorio-client-javascript';
import getConfig from 'next/config';

import {
  BRAND_SUBSECTIONS_TYPE,
  VERTICAL_LIST_CAROUSEL_PRICE_LIST_SECTION,
  VERTICAL_LIST_CAROUSEL_RANK_LIST_SECTION,
} from 'homepage/enums/contentConstants';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { buildRequestUrl, getPageLimit, isCollection } from 'homepage/utils/buildRequestUrl';
import { CONSTRUCTOR_SERVICE_URL } from 'search/enums/constants';
import { requestService } from 'shared/utils/services/requestService';
import { setBrandsListsCountsOnSubsectionsClientSide } from 'homepage/utils/brandsListsCounts/brandsListCountsClientSide';
import { IFeedBrandFeed, IFeedHeroSection } from 'homepage/types/Feed';
import { ISpaceArticleSection } from 'shared/types/SpaceArticleSection';

export const fetchFeed = async (slug: string, segmentIds: string[]) => {
  const response: IFeedBrandFeed = await requestService({
    url: `/web-api/v1/cms/cms_articles/${slug}`,
    method: 'GET',
  });

  const feedSections = response.sections;
  const authorizedFeedSections = feedSections?.filter(
    (section: IFeedHeroSection | ISpaceArticleSection) => {
      return !section?.segmentId || segmentIds?.includes(section?.segmentId);
    },
  );

  return {
    draftId: response?.draftId,
    draftUuid: response?.draftUuid,
    id: response?.id,
    name: response.name,
    sections: authorizedFeedSections,
    uuid: response?.uuid,
  };
};

const getConstructorClient = () => {
  const { publicRuntimeConfig } = getConfig();

  const constructorIo = new ConstructorIOClient({
    apiKey: publicRuntimeConfig.CONSTRUCTOR_API_KEY,
    sendTrackingEvents: true,
    serviceUrl: CONSTRUCTOR_SERVICE_URL,
    idOptions: {
      client_id_storage_location: 'cookie',
      session_id_storage_location: 'cookie',
    },
  });
  return constructorIo;
};

export const fetchProductsForHomepageModule = async (
  searchConfig,
  headers,
  rowCount = 0,
  abortSignal,
  enableSearchLanding,
) => {
  let requestUrl;
  let requestBody;
  const { collectionId, options } = searchConfig || {};
  try {
    if (enableSearchLanding && isCollection(searchConfig)) {
      requestUrl = `/web-api/consumer-search/collection-content`;
      requestBody = {
        slug: collectionId,
        pageLimit: getPageLimit({
          rowCount: rowCount > 0 ? rowCount : undefined,
          isProductGridSection: rowCount <= 0,
          isProductCarouselSection: rowCount > 0,
          options,
        }),
        pageNumber: 1,
      };
    } else {
      requestUrl =
        rowCount > 0
          ? buildRequestUrl({
              searchConfig,
              isProductGridSection: false,
              isProductCarouselSection: true,
              rowCount,
            })
          : buildRequestUrl({ searchConfig, isProductGridSection: true });
    }

    // this section passes params to build the request to get the results for this section
    // it was more straight forward to pass the 4 params to a string then to transition it
    // to be used by the js client for constructor

    const productResponse = await axios
      .get(requestUrl, {
        headers,
        signal: abortSignal,
        params: requestBody,
      })
      .then((response) => {
        return response.data;
      });

    return productResponse;
  } catch (error) {
    bugsnag(error, { context: 'fetchProductsForHomepageModule' });
  }
};

export const fetchBrandListSection = async (section, subsections, headers, abortSignal) => {
  try {
    if (subsections?.[0].type === BRAND_SUBSECTIONS_TYPE) {
      const subsectionsWithCounts = await setBrandsListsCountsOnSubsectionsClientSide(
        subsections,
        headers,
        abortSignal,
      );
      const newSection = { ...section };

      newSection.subsections = subsectionsWithCounts;

      return newSection;
    }
  } catch (error) {
    bugsnag(error, { context: 'fetchBrandListSection' });
  }
};

export const fetchCategoryListSection = async (searchConfig, headers) => {
  const ConstructorClient = getConstructorClient();

  try {
    const { filterKey, filterValue } = searchConfig;

    const constructorResponse = await ConstructorClient.browse.getBrowseResults(
      filterKey,
      filterValue,
      {
        resultsPerPage: 4,
        preFilterExpression: null,
      },
      headers,
    );

    return constructorResponse;
  } catch (error) {
    bugsnag(error, { context: 'fetchCategoryListSection' });
  }
  return null;
};

export const fetchDetailedProductListSection = async ({
  searchConfig,
  headers,
  abortSignal,
  enableSearchLanding,
}) => {
  let requestUrl;
  let requestBody;
  const isCollectionGrid = isCollection(searchConfig);
  const canCallConsumerSearch = enableSearchLanding && isCollectionGrid;

  try {
    const { collectionId, options } = searchConfig || {};
    if (canCallConsumerSearch) {
      requestUrl = `/web-api/consumer-search/collection-content`;
      requestBody = {
        slug: collectionId,
        pageLimit: getPageLimit({
          isProductGridSection: false,
          options,
        }),
        pageNumber: 1,
      };
    } else {
      requestUrl = buildRequestUrl({
        searchConfig,
        isProductGridSection: false,
      });
    }

    // this section passes params to build the request to get the results for this section
    // it was more straight forward to pass the 4 params to a string then to transition it
    // to be used by the js client for constructor
    const logHeaders = {
      'x-goat-collection-api': enableSearchLanding ? 'consumer-search' : 'constructor-api',
    };
    performance.mark('collection-content-start');

    const constructorResponse = await requestService({
      url: requestUrl,
      headers: {
        ...headers,
        ...(isCollectionGrid ? logHeaders : {}),
      },
      signal: abortSignal,
      params: requestBody,
    }).then((response) => {
      performance.mark('collection-content-end');
      return response;
    });
    if (isCollectionGrid) {
      performance.measure(
        // eslint-disable-next-line no-nested-ternary
        constructorResponse?.error
          ? '_error_'
          : enableSearchLanding
          ? 'consumer-search'
          : 'constructor-api',
        'collection-content-start',
        'collection-content-end',
      );
    }

    return constructorResponse;
  } catch (error) {
    performance.mark('collection-content-end');
    performance.measure('_error_', 'collection-content-start', 'collection-content-end');
    bugsnag(error, { context: 'fetchDetailedProductListSection' });
  }
};

export const fetchVerticalListSection = async (
  searchConfig,
  type,
  headers,
  abortSignal,
  enableSearchLanding,
) => {
  const isRanked = type === VERTICAL_LIST_CAROUSEL_RANK_LIST_SECTION;
  const isPriced = type === VERTICAL_LIST_CAROUSEL_PRICE_LIST_SECTION;
  try {
    let requestUrl;
    let requestBody;
    const isCollectionGrid = isCollection(searchConfig);
    const canCallConsumerSearch = enableSearchLanding && isCollectionGrid;
    const { collectionId, options } = searchConfig || {};

    const logHeaders = {
      'x-goat-collection-api': enableSearchLanding ? 'consumer-search' : 'constructor-api',
    };

    if (isRanked || isPriced) {
      if (canCallConsumerSearch && isRanked) {
        requestUrl = `/web-api/consumer-search/collection-content`;
        requestBody = {
          slug: collectionId,
          pageLimit: getPageLimit({
            options,
          }),
          pageNumber: 1,
        };
      } else {
        requestUrl = buildRequestUrl({
          searchConfig,
        });
      }

      // this section passes params to build the request to get the results for this section
      // it was more straight forward to pass the 4 params to a string then to transition it
      // to be used by the js client for constructor
      performance.mark('collection-content-start');
      const constructorResponse = await requestService({
        url: requestUrl,
        headers: {
          ...headers,
          ...(isCollectionGrid ? logHeaders : {}),
        },
        signal: abortSignal,
        params: requestBody,
      }).then((response) => {
        performance.mark('collection-content-end');
        return response;
      });
      if (isCollectionGrid && isRanked) {
        performance.measure(
          // eslint-disable-next-line no-nested-ternary
          constructorResponse?.error
            ? '_error_'
            : enableSearchLanding
            ? 'consumer-search'
            : 'constructor-api',
          'collection-content-start',
          'collection-content-end',
        );
      }
      return constructorResponse;
    }
  } catch (error) {
    if (isRanked || isPriced) {
      performance.mark('collection-content-end');
      performance.measure('_error_', 'collection-content-start', 'collection-content-end');
    }
    bugsnag(error, { context: 'Homepage Content Fetcher Vertical List Section' });
  }
  return null;
};
